/**
 * Compact experience card used by the NEW_UX Experiences overview grid and by the
 * user profile's Experiences tab, so both surfaces render the same card shell
 * (status-grouped grid, chips row, two-line title, meta row).
 */
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import React from 'react';
import { CardWithHover } from '../../../../components/CardWitHover';

export const ExperienceCard = styled(
  React.forwardRef<
    any,
    React.ComponentProps<typeof CardWithHover> & { clickable?: boolean }
  >(({ clickable, ...props }, ref) => <CardWithHover {...props} ref={ref} />)
)<{ clickable?: boolean }>`
  background: ${(p) => p.theme.custom.colors.background.light};
  /* && beats the theme's MuiCard root padding (spacing(3) = 24px) which
     leaves the compact card feeling empty. */
  && {
    padding: ${(p) => p.theme.spacing(1.5)}px ${(p) => p.theme.spacing(2)}px;
    /* MuiCard-root clips overflow by default. Nothing here needs edge-to-edge
       clipping (no full-bleed image etc.), and the CopilotBadge's own gradient-border
       wrapper renders taller than its sibling status chip, which the compact padding
       above doesn't leave enough room for — clipping its corner against the card edge. */
    overflow: visible;
  }
  /* Skip layout + paint for cards that are off-screen, and reserve their height
     so the scrollbar stays accurate and nothing shifts (no CLS). For a large org
     this is the main win: the browser only renders the handful of cards near the
     viewport on first paint instead of laying out and painting all of them, then
     renders the rest as they scroll in. The intrinsic-size "auto" keyword latches
     each card's real size after its first render, so the 152px is only an estimate
     for cards that have never been on-screen. Gracefully ignored on browsers
     without content-visibility. OverviewClassic has its own card. */
  content-visibility: auto;
  contain-intrinsic-size: auto 152px;

  /* By default the card is not the click target — its buttons do the
     navigating — so neutralize CardWithHover's pointer cursor + hover/active
     ring so the whole card doesn't read as clickable. \`clickable\` emits no
     overrides, leaving CardWithHover's own rules in place for surfaces where
     the card itself opens something. */
  cursor: ${(p) => (p.clickable ? undefined : 'default')};

  &:hover {
    box-shadow: ${(p) => (p.clickable ? undefined : 'none')};
  }
  &:active {
    background-color: ${(p) => (p.clickable ? undefined : 'transparent')};
    box-shadow: ${(p) => (p.clickable ? undefined : 'none')};
  }
`;

export const ExperienceCardGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(3)}px;
  grid-row-gap: ${(p) => p.theme.spacing(3)}px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const ExperienceCardStatusGroup = styled('div')`
  &:not(:last-of-type) {
    margin-bottom: ${(p) => p.theme.spacing(5)}px;
  }
`;

// && beats MuiTypography-root's `margin: 0`, which Material UI injects after
// emotion's styles.
export const ExperienceCardStatusHeading = styled(Typography)`
  && {
    margin-bottom: ${(p) => p.theme.spacing(2)}px;
  }
` as typeof Typography;

export const ExperienceCardHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(p) => p.theme.spacing()}px;
  margin-bottom: ${(p) => p.theme.spacing()}px;
`;

export const ExperienceCardChipsRow = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spacing()}px;
  min-width: 0;
`;

export const ExperienceCardTitle = styled(Typography)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  height: calc(${(p) => p.theme.typography.h4.lineHeight} * 2);
  && {
    margin-bottom: ${(p) => p.theme.spacing(0.5)}px;
  }
` as typeof Typography;

// Meta line under the title. The overview renders it empty but keeps the row so
// it reserves one line of height and card heights stay stable; the profile
// fills it with the instance's dates.
export const ExperienceCardMetaRow = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing()}px;
  min-height: ${(p) => p.theme.custom.fontSize.s * 1.5}px;
`;
